import type { AttributeMap, Delta, Op } from 'quill/core';
import Quill from 'quill/core';
import { useLayoutEffect, useState } from 'react';
import { useOptimisticItemContentRects } from './useOptimisticItemContentRects';
import { useQuill } from '../editor/QuillContext';
import isInlineTextInsertion from '../editor/utils/isInlineTextInsertion';
import type { DocumentType } from '../graphql/generated';
import { gql } from '../graphql/generated';

const POST_FRAGMENT = gql(`
  fragment useHasMagneticWallItems_post on PostFields {
    id
    threads {
      id
    }
    annotations {
      id
    }
  }
`);

const walkTree = (value: unknown, visit: (value: unknown) => void) => {
  if (value == null) return;

  visit(value);

  if (Array.isArray(value)) {
    value.forEach((v) => walkTree(v, visit));
  } else if (typeof value === 'object') {
    Object.values(value).forEach((v) => walkTree(v, visit));
  }
};

const collectAttributes = (op: Op) => {
  const attributes: AttributeMap[] = [];
  // we need to deeply traverse delta ops to find all attributes
  // that are for example in nested ops in table-embed or code-embed
  walkTree(op, (value) => {
    if (
      value &&
      typeof value === 'object' &&
      'attributes' in value &&
      value.attributes !== null &&
      typeof value.attributes === 'object'
    ) {
      attributes.push(value.attributes as AttributeMap);
    }
  });

  return attributes;
};

const toStringArray = (value: unknown) => {
  return Array.isArray(value)
    ? value.filter((id) => typeof id === 'string')
    : [];
};

const collectAllAttributeIds = (ops: Op[]) => {
  const allThreadIds = new Set<string>();
  const allAnnotationIds = new Set<string>();

  ops.forEach((op) => {
    collectAttributes(op).forEach(({ comment, reaction, annotation }) => {
      toStringArray(comment).forEach((id) => allThreadIds.add(id));
      toStringArray(reaction).forEach((id) => allThreadIds.add(id));
      toStringArray(annotation).forEach((id) => allAnnotationIds.add(id));
    });
  });

  return {
    allThreadIds,
    allAnnotationIds,
  };
};

export const hasThreadsOrAnnotationsInOps = (
  ops: Op[],
  threadIds: Set<string>,
  annotationIds: Set<string>,
) => {
  const result = collectAllAttributeIds(ops);

  const hasThreads = threadIds.intersection(result.allThreadIds).size > 0;
  const hasAnnotations =
    annotationIds.intersection(result.allAnnotationIds).size > 0;

  return hasThreads || hasAnnotations;
};

const useHasMagneticWallItems = (
  post: DocumentType<typeof POST_FRAGMENT> | undefined,
  skip?: boolean,
) => {
  const { quill } = useQuill();

  const [hasThreadsOrAnnotations, setHasThreadsOrAnnotations] = useState(false);
  const optimisticItemContentRects = useOptimisticItemContentRects();
  const hasOptimisticItemContentRects = optimisticItemContentRects.size > 0;

  useLayoutEffect(() => {
    if (!quill || skip) return undefined;

    const checkAndSetMagneticWallItemState = () => {
      const threadIds = new Set(post?.threads.map((thread) => thread.id));
      const annotationIds = new Set(
        post?.annotations.map((annotation) => annotation.id),
      );

      setHasThreadsOrAnnotations(
        hasThreadsOrAnnotationsInOps(
          quill.getContents().ops,
          threadIds,
          annotationIds,
        ),
      );
    };

    checkAndSetMagneticWallItemState();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      checkAndSetMagneticWallItemState();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill, post, skip]);

  return !skip && (hasThreadsOrAnnotations || hasOptimisticItemContentRects);
};

export default useHasMagneticWallItems;
